import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { pushTraceLogEvent } from '../Component/Dashboard/sessionEventPush';
import { Config } from '../Component/Common/Config';

/**
 * useBookingSession — admin booking portal session timer hook.
 *
 * Mirrors the patient portal's hooks/useBookingSession.js with two adaptations:
 *   1. Settings are fetched from the admin API (Config.url + 'settings') instead
 *      of the open/public endpoint.
 *   2. The extend endpoint uses Config.urlLogin instead of Config.urlOpen.
 *
 * @param {Function} onSessionExpired - Callback invoked when the session reaches 0.
 */
export const useBookingSession = (onSessionExpired) => {
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [countdownStartTime, setCountdownStartTime] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [settings, setSettings] = useState({
    duration_seconds: 420,
    warning_before_seconds: 30,
    extension_duration_seconds: 300,
    max_extensions: 2,
  });
  const [extensionsUsed, setExtensionsUsed] = useState(0);
  const [maxExtensionsReached, setMaxExtensionsReached] = useState(false);

  // refs — avoid stale closures inside setInterval
  const timerRef = useRef(null);
  const sessionDurationMsRef = useRef(0);
  const warningLoggedRef = useRef(false);
  const extensionCountRef = useRef(0); // mirrors extensionsUsed — safe to read inside interval

  // Keep extensionCountRef in sync with state
  useEffect(() => {
    extensionCountRef.current = extensionsUsed;
  }, [extensionsUsed]);

  // ─── Fetch admin session settings ──────────────────────────────────────────
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${Config.url}settings`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const bs = response?.data?.data?.booking_session;
      let parsed = null;

      if (bs && !Array.isArray(bs) && typeof bs === 'object') {
        // Flat-object format (current API)
        parsed = {
          duration_seconds:           parseInt(bs.duration_seconds)           || 420,
          warning_before_seconds:     parseInt(bs.warning_before_seconds)     || 30,
          extension_duration_seconds: parseInt(bs.extension_duration_seconds) || 300,
          max_extensions:             parseInt(bs.max_extensions)             || 2,
        };
      } else if (bs && Array.isArray(bs) && bs.length > 0) {
        // Legacy array format
        const getValue = (key) =>
          bs.find((s) => s.key_name === `booking_session.${key}` || s.key_name === key)?.value;
        parsed = {
          duration_seconds:           parseInt(getValue('duration_seconds'))           || 420,
          warning_before_seconds:     parseInt(getValue('warning_before_seconds'))     || 30,
          extension_duration_seconds: parseInt(getValue('extension_duration_seconds')) || 300,
          max_extensions:             parseInt(getValue('max_extensions'))             || 2,
        };
      }

      if (parsed) {
        setSettings(parsed);
        return parsed;
      }
    } catch (error) {
      console.error('[useBookingSession] Failed to fetch booking session settings:', error);
    }
    return settings; // return current value as fallback
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Start a fresh booking session ─────────────────────────────────────────
  const startSession = useCallback(async () => {
    const fetchedSettings = await fetchSettings();
    const durationMs = fetchedSettings.duration_seconds * 1000;

    // Clear any running timer before starting a new one
    if (timerRef.current) clearInterval(timerRef.current);

    sessionDurationMsRef.current = durationMs;
    warningLoggedRef.current = false; // reset warning gate — ONLY reset here and in extendSession

    setCountdownStartTime(Date.now());
    setTimeRemaining(durationMs);
    setExtensionsUsed(0);
    setMaxExtensionsReached(false);
    setShowWarningModal(false);
  }, [fetchSettings]);

  // ─── Stop the session (e.g. on booking confirmed or component unmount) ──────
  const stopSession = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    sessionDurationMsRef.current = 0;
    setCountdownStartTime(null);
    setTimeRemaining(0);
    setShowWarningModal(false);
    setExtensionsUsed(0);
    setMaxExtensionsReached(false);
  }, []);

  // ─── Extend the session ─────────────────────────────────────────────────────
  const extendSession = useCallback(async () => {
    const currentExtCount = extensionCountRef.current;
    const extensionMs = settings.extension_duration_seconds * 1000;

    const applyLocalExtension = (newExtensionsUsed) => {
      setExtensionsUsed(newExtensionsUsed);
      sessionDurationMsRef.current = extensionMs;
      warningLoggedRef.current = false;
      setCountdownStartTime(Date.now());
      setTimeRemaining(extensionMs);
      setShowWarningModal(false);
    };

    try {
      const sessionId = localStorage.getItem('bookingSessionId');
      if (!sessionId) {
        applyLocalExtension(currentExtCount + 1);
        return;
      }

      const traceSessionId = localStorage.getItem('trace_log_id') || sessionId;

      const response = await axios.post(
        `${Config.urlLogin}api/v1/booking-session/extend`,
        {
          slot_session_id: sessionId,
          extension_seconds: settings.extension_duration_seconds,
          trace_session_id: traceSessionId,
        },
        { headers: { 'Content-Type': 'application/json' } },
      );

      if (response?.data?.success !== false) {
        const newExtensionsUsed = response.data?.extensions_used ?? (currentExtCount + 1);
        applyLocalExtension(newExtensionsUsed);

        pushTraceLogEvent(
          {
            event: 'session_extended',
            payload: {
              slot_session_id: sessionId,
              extension_count: newExtensionsUsed,
              popup_number: currentExtCount + 1,
              popup_label: `${currentExtCount} of ${settings.max_extensions}`,
              extensions_used: newExtensionsUsed,
              max_extensions: settings.max_extensions,
              is_final_window: newExtensionsUsed >= settings.max_extensions,
              extension_duration_seconds: settings.extension_duration_seconds,
            },
            step_status: 'success',
          },
          null,
          traceSessionId,
        );
      }
    } catch (error) {
      console.error('[useBookingSession] Failed to extend session:', error);
      if (error?.response?.status === 422) {
        // Backend says max extensions reached — sync frontend
        setMaxExtensionsReached(true);
        pushTraceLogEvent(
          {
            event: 'session_extension_declined',
            payload: {
              extension_count: extensionCountRef.current + 1,
              reason: 'max_extensions_reached_by_backend',
            },
            step_status: 'success',
          },
          null,
          localStorage.getItem('trace_log_id') || localStorage.getItem('bookingSessionId'),
        );
      } else {
        // Non-422 — frontend timer already reset; just increment count and continue
        applyLocalExtension(currentExtCount + 1);
        console.warn('[useBookingSession] booking-session/extend failed; frontend-only extension applied:', error?.message);
      }
    }
  }, [settings]);

  // ─── Decline extension (user says "No, let it expire") ─────────────────────
  const declineExtension = useCallback(() => {
    setShowWarningModal(false);
    pushTraceLogEvent(
      {
        event: 'session_extension_declined',
        payload: {
          extension_count: extensionCountRef.current + 1,
          extensions_used: extensionCountRef.current,
          max_extensions: settings.max_extensions,
        },
        step_status: 'success',
      },
      null,
      localStorage.getItem('trace_log_id') || localStorage.getItem('bookingSessionId'),
    );
  }, [settings.max_extensions]);

  // ─── Countdown interval ─────────────────────────────────────────────────────
  // Key rules (mirrors patient hook):
  //   1. Always clear before creating — prevents ghost intervals.
  //   2. Read duration from sessionDurationMsRef — avoids stale closure.
  //   3. warningLoggedRef is NEVER reset here — only in startSession / extendSession.
  useEffect(() => {
    if (countdownStartTime && sessionDurationMsRef.current > 0) {
      if (timerRef.current) clearInterval(timerRef.current);

      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - countdownStartTime;
        const remaining = Math.max(0, sessionDurationMsRef.current - elapsed);
        setTimeRemaining(remaining);

        // Show warning once per session window
        const totalMs = sessionDurationMsRef.current;
        const rawWarningMs = settings.warning_before_seconds * 1000;
        // Enforce minimum 30 s gap so warning never fires at session start
        const warningMs = Math.min(rawWarningMs, totalMs - 30000);

        if (remaining <= warningMs && remaining > 0 && !warningLoggedRef.current) {
          warningLoggedRef.current = true;
          setShowWarningModal(true);

          const popupNumber = extensionCountRef.current + 1;
          pushTraceLogEvent(
            {
              event: 'session_warning_shown',
              payload: {
                warning_before_seconds: settings.warning_before_seconds,
                time_remaining_seconds: Math.round(remaining / 1000),
                popup_number: popupNumber,
                popup_label: `${extensionCountRef.current} of ${settings.max_extensions}`,
                extensions_used: extensionCountRef.current,
                max_extensions: settings.max_extensions,
                is_final_window: extensionCountRef.current >= settings.max_extensions,
              },
              step_status: 'success',
            },
            null,
            localStorage.getItem('trace_log_id') || localStorage.getItem('bookingSessionId'),
          );
        }

        if (remaining === 0) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          if (onSessionExpired) {
            onSessionExpired();
          }
        }
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdownStartTime, settings]);

  return {
    timeRemaining,
    countdownStartTime,
    showWarningModal,
    settings,
    extensionsUsed,
    maxExtensionsReached,
    startSession,
    stopSession,
    extendSession,
    declineExtension,
  };
};

export default useBookingSession;
